import { VHVideoConfig } from './config';
import Component from './core/Component';
import Store from './core/Store';
import { AutoDetectInitial, UserCustomInitial, FlashInitial } from './player/createPlayer';
import { PlayerEvent } from './PlayerEvents';
import PluginMap from './plugins/PluginMap';
import log from 'loglevel';
import { systemProbe } from './utils/util';

const TENCENT_DELAY = 90;

/**
 * 播放器模块
 *
 * @export
 * @class VideoModule
 * @extends {Component}
 */
export default class VideoModule extends Component {
  constructor() {
    super();
    this.player = null; // FlvPlayer | HlsPlayer | NativePlayer | FlashPlayer
    this._originalOption = {};
    this._config = {};
    this.store = new Store();
    this.pluginInstance = [];
    this.schedulerPluginInstance = null;
    // 探针判断系统环境
    this.context = systemProbe();
    log.info('systemProbe()兼容性检测结果: ', JSON.parse(JSON.stringify(this.context)));
  }

  async init(option = {}) {
    this.timeline = option.timeline;
    this._originalOption = option;
    this._config = this._configMapping(JSON.parse(JSON.stringify(option)));
    log.info('初始化播放器内核参数： ', JSON.parse(JSON.stringify(this._config)));

    // 初始化底层播放器。如用户未传入，则默认判断逻辑，否则按照用户传入处理
    let initializer;
    if (this._config.useSWF) {
      initializer = new FlashInitial(this);
    } else if (this._config.type === 'auto' && this._config.isLive) {
      initializer = new AutoDetectInitial(this);
    } else {
      initializer = new UserCustomInitial(this);
    }
    this.player = await initializer.init(this.context, this._config); //注意，创建内核播放器时可能引用修改this._config.type，比如auto->flv
    if (!this.player) {
      log.error('初始化播放器内核player失败');
      return;
    }

    // 初始化事件监听
    this.initListener();
    // 加载插件
    this._pluginCall();
  }

  _configMapping(option = {}) {
    let config = {};
    if (option['plugin_timeshift']) {
      const timeshiftConfig = JSON.parse(option['plugin_timeshift']);
      option.isOpenTimeshift = timeshiftConfig['isOpenTimeshift'];
      option.live_duration = timeshiftConfig['live_duration'];
    }
    if (option['isLive']) {
      option.useSWF = option['liveOption'] && option['liveOption']['useSWF'];
    } else {
      option.useSWF = option['vodOption'] && option['vodOption']['useSWF'];
    }
    // 是否使用flash，如用户未传入，则默认判断逻辑，否则按照用户传入处理
    option.useSWF =
      option.useSWF === undefined ? this.context.browser === 'ie' || config.type === 'rtmp' : option.useSWF;
    Object.assign(config, VHVideoConfig, option);
    return config;
  }

  _pluginCall() {
    if (!this.player) {
      log.info('warn', '播放器为空，无法初始化插件');
      return;
    }
    PluginMap.forEach((clazz, key) => {
      let cl = new clazz();
      cl.player = this;
      cl.init(this._config);
      this.pluginInstance.push(cl);
      if (key == 'plugin_scheduler') {
        this.schedulerPluginInstance = cl;
      }
    });
  }

  /**
   * 重新播放调度
   */
  refreshDispatch() {
    this.schedulerPluginInstance && this.schedulerPluginInstance.refreshDispatch();
  }

  destroy() {
    super.destroy();

    this.removeAllEvents();
    // 清理插件
    this.pluginInstance.forEach(plugin => {
      plugin.destroy();
    });

    this.pluginInstance = [];
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
  }

  setSize(w, h) {
    const parent = this.player.getRoot();
    if (!parent) {
      log.warn('无法找到root元素');
      return;
    }

    parent.style.width = w;
    parent.style.height = h;
    this.pluginInstance.forEach(plugin => {
      plugin.setSize(w, h);
    });
  }

  initListener() {
    const self = this;

    // 监听快直播降级事件， 快直播rtl降级内核时，会触发 PlayerEvent.CHANGEKERNEL 事件
    this.on(PlayerEvent.CHANGEKERNEL, async () => {
      // 创建新的内核，flv，hls内核自适应选择
      this._config.type = 'auto';
      this._config.liveOption.type = 'auto';
      const initializer = new AutoDetectInitial(this);
      this.player = await initializer.init(this.context, this._config);
      const changeType = this.player.CLASS_NAME === 'NativePlayer' ? 'hls' : 'flv';

      // 降级内核后，转发PlayerEvent.CHANGEKERNELDATA，重新解析调度数据
      this.emit(PlayerEvent.CHANGEKERNELDATA, changeType);
    });

    // 监听调度完成事件
    this.on(PlayerEvent.SCHEDULER_COMPLETE, () => {
      log.info('收到播放调度完成事件');
      const def = self.currentDefinition || {};
      log.info(`当前播放线路： ${JSON.stringify(def)}`);
      //在Token.js中增加此属性赋值
      const token = self.newToken;
      let url = `${def.url}?token=${token}`;
      this.player.cdnName = def.cdnName;
      // 腾讯cdn时移直播
      if (
        this._config.isLive &&
        this._config.isOpenTimeshift &&
        this._config.type === 'hls' &&
        this.player.cdnName === 'tx'
      ) {
        url = `${def.url}?delay=${TENCENT_DELAY}&token=${token}`;
        this.player.delay = TENCENT_DELAY;
      }
      // 设置播放URL
      this.player.setSrc(url);

      if (this._config.autoplay) {
        setTimeout(() => {
          if (
            this.context['os'] === 'ios' &&
            this.context.browser !== 'wechat' &&
            this.player.CLASS_NAME == 'NativePlayer'
          ) {
            // ios系统 + 非微信环境 + NativePlayer播放。兼容性处理，chrome50+ play()方法才返回promise
            const playPromise = this.play();
            if (playPromise !== undefined) {
              log.info('ios NativePlayer 环境下执行自动播放:', this.player.getMuted());
              log.info('====>ios wechat 环境下this.player', this.player.getMuted());
              playPromise
                .then(() => {
                  log.info('====>ios NativePlayer 环境下自动播放成功');
                  this.emit2All(PlayerEvent.AUTOPLAY_SUCCESS, { isMuted: this.player.getMuted() });
                })
                .catch(e => {
                  if (!this.player.getMuted() && this._config.autoplayMuteAttempt) {
                    // 自动播放失败，设置静音后重试播放
                    log.info('====>ios NativePlayer 环境下自动播放失败，设置静音后重试播放');
                    this.setMuted(true);
                    this.play()
                      .then(() => {
                        log.info('====>ios NativePlayer 环境下设置静音后自动播放成功，并触发 AUTOPLAY_SUCCESS 事件');
                        this.emit2All(PlayerEvent.AUTOPLAY_SUCCESS, { isMuted: this.player.getMuted() });
                      })
                      .catch(error => {
                        log.info('====>ios NativePlayer 环境下设置静音后自动播放依然失败');
                        this.emit2All(PlayerEvent.AUTOPLAY_FAILED, { isMuted: this.player.getMuted() });
                        // 静音还原回去，让用户手动点击时有声音
                        this.setMuted(false);
                      });
                  } else {
                    log.info('====>ios NativePlayer 环境下自动播放失败0');
                    this.emit2All(PlayerEvent.AUTOPLAY_FAILED, { isMuted: this.player.getMuted() });
                  }
                });
            }
          } else if (this.context['os'] === 'ios' && this.context.browser == 'wechat') {
            // ios系统 + 微信环境
            const autoPlay = () => {
              WeixinJSBridge.invoke('getNetworkType', {}, res => {
                this.play()
                .then(() => {
                  log.info('====>IOS微信环境自动播放成功');
                  this.emit2All(PlayerEvent.AUTOPLAY_SUCCESS, { isMuted: this.video.muted });
                })
                .catch(error => {
                  log.info('====>IOS微信环境自动播放失败');
                  this.emit2All(PlayerEvent.AUTOPLAY_FAILED, { isMuted: this.video.muted });
                });
              });
            };
            if (WeixinJSBridge) {
              autoPlay();
            } else {
              document.addEventListener('WeixinJSBridgeReady', autoPlay);
            }
          }
        }, 100);
      }
    });
  }

  async setQuality(newValue) {
    const e = {
      oldValue: JSON.parse(JSON.stringify(this.currentDefinition)),
      newValue
    };
    let token = this.newToken;
    let url = `${newValue.url}?token=${token}`;
    this.currentDefinition = { noSet: true, ...newValue };
    // 时移直播
    if (
      this._config.isLive &&
      this._config.isOpenTimeshift &&
      this._config.type === 'hls' &&
      this.player.getCdnName() === 'tx'
    ) {
      url = `${newValue.url}?delay=${this.player.delay}&token=${token}`;
    }

    // 点播记录换清晰度时的时间,用于判断是否向文档派发timeupdate事件
    if (!this._config.isLive && !this.player.vodPlayedTime) {
      this.player.vodPlayedTime = this.player.getCurrentTime();
    }
    this.player.emit2All(PlayerEvent.DEFINITION_CHANGED, e);
    try {
      await this.player.setSrc(url);
      // 手动换清晰度在这里会重复seek,封装的内核里已经seek了一次。
      this.player.vodPlayedTime && this.player.setCurrentTime(this.player.vodPlayedTime);
    } catch (error) {
      log.warn('切源失败');
    }
  }

  get version() {
    return process.env.VERSION;
  }
}
